import { setUserId as setAnalyticsUserId } from "firebase/analytics";
import { User as FirebaseUser } from "firebase/auth";
import posthog from "posthog-js";
import { useEffect } from "react";

import { getFirebase } from "../configs/firebase";
import { Organization, User } from "../store/pomeriumApi";
import {
  captureProductEvent,
  clearPendingEmailVerification,
  hasPendingEmailVerification,
  normalizeAuthProvider,
  productAnalyticsEvents,
} from "./events";

type AuthenticatedAnalyticsBootstrapProps = {
  firebaseUser: FirebaseUser;
  organization?: Organization;
  pomeriumUser: User;
};

export default function AuthenticatedAnalyticsBootstrap({
  firebaseUser,
  organization,
  pomeriumUser,
}: AuthenticatedAnalyticsBootstrapProps) {
  const { analytics } = getFirebase();

  useEffect(() => {
    if (!firebaseUser.emailVerified || !pomeriumUser.id) {
      return;
    }

    if (analytics) {
      setAnalyticsUserId(analytics, pomeriumUser.id);
    }

    posthog.identify(pomeriumUser.id, {
      email: pomeriumUser.email,
      firebase_uid: firebaseUser.uid,
      name: pomeriumUser.displayName || firebaseUser.displayName || undefined,
      needs_onboarding: pomeriumUser.needsOnboarding,
      user_type: pomeriumUser.type,
    });
  }, [
    analytics,
    firebaseUser.displayName,
    firebaseUser.emailVerified,
    firebaseUser.uid,
    pomeriumUser.displayName,
    pomeriumUser.email,
    pomeriumUser.id,
    pomeriumUser.needsOnboarding,
    pomeriumUser.type,
  ]);

  useEffect(() => {
    if (!firebaseUser.emailVerified || !pomeriumUser.id || !organization?.id) {
      return;
    }

    posthog.group("organization", organization.id, {
      name: organization.name,
      organization_role: organization.role,
      organization_type: organization.organizationType,
      read_only: organization.readOnly,
    });
  }, [
    firebaseUser.emailVerified,
    organization?.id,
    organization?.name,
    organization?.organizationType,
    organization?.readOnly,
    organization?.role,
    pomeriumUser.id,
  ]);

  useEffect(() => {
    if (!firebaseUser.emailVerified || !hasPendingEmailVerification(firebaseUser.uid)) {
      return;
    }

    clearPendingEmailVerification(firebaseUser.uid);
    captureProductEvent(productAnalyticsEvents.authEmailVerified, {
      auth_provider: normalizeAuthProvider(firebaseUser.providerData?.[0]?.providerId),
    });
    posthog.capture("email_verified");
  }, [firebaseUser.emailVerified, firebaseUser.providerData, firebaseUser.uid]);

  return null;
}
